export interface NavItem {
  name: string;
  path?: string;
  children?: string[];
  adminOnly?: boolean;
}

const ROOT_ID = 'nav';

const ALL_ITEMS: Record<string, NavItem> = {
  [ROOT_ID]: {
    name: 'Navigation',
    children: [
      'employee',
      'office',
      'admin-project-settings',
      'cost-groups',
      'employee-project-models',
      'source-data',
    ],
  },
  employee: {
    name: 'Employee Settings',
    adminOnly: true,
    children: ['employees', 'working-time-model'],
  },
  employees: {
    name: '👤 Employee',
    path: '/settings/employees',
    adminOnly: true,
  },
  'working-time-model': {
    name: '🕙 Working Time Model',
    path: '/settings/working-time-model',
    adminOnly: true,
  },
  office: {
    name: 'Office Settings',
    adminOnly: true,
    children: ['locations', 'holidays'],
  },
  locations: {
    name: '🏢 Location ',
    path: '/settings/locations',
    adminOnly: true,
  },
  holidays: {
    name: '🏖️ Holiday',
    path: '/settings/holidays',
    adminOnly: true,
  },
  'admin-project-settings': {
    name: 'Project Settings',
    adminOnly: true,
    children: ['projects', 'orders', 'tasks'],
  },
  projects: {
    name: '📚 Project',
    path: '/settings/projects',
    adminOnly: true,
  },
  orders: {
    name: '📨 Order',
    path: '/settings/orders',
    adminOnly: true,
  },
  tasks: {
    name: '🗃️ Task/ WBS',
    path: '/settings/tasks',
    adminOnly: true,
  },
  'cost-groups': {
    name: 'Cost Group Settings',
    adminOnly: true,
    children: ['cost-groups-list', 'cost-group-rates', 'cost-group-employees'],
  },
  'cost-groups-list': {
    name: '👥 Cost groups',
    path: '/settings/cost-groups',
    adminOnly: true,
  },
  'cost-group-rates': {
    name: '🗓️ Year/ Rates',
    path: '/settings/cost-groups/year-rates',
    adminOnly: true,
  },
  'cost-group-employees': {
    name: '🪙 Employee cost group',
    path: '/settings/cost-groups/employee-cost-group',
    adminOnly: true,
  },
  'employee-project-models': {
    name: '📰 Report Settings',
    path: '/project-settings/employee-project-models',
  },
  'source-data': {
    name: 'Source',
    adminOnly: true,
    children: ['org-structure', 'project-properties', 'vendor', 'customer', 'wbs-details'],
  },
  'org-structure': {
    name: '🖧 Org Structure',
    path: '/settings/org-structure',
    adminOnly: true,
  },
  'project-properties': {
    name: '🗃️ Project Properties',
    path: '/settings/project-properties',
    adminOnly: true,
  },
  vendor: {
    name: '👨🏻‍💼 Vendor',
    path: '/settings/vendor',
    adminOnly: true,
  },
  customer: {
    name: '🏭 Customer',
    path: '/settings/customer',
    adminOnly: true,
  },
  'wbs-details': {
    name: 'ᯓ WBS Details',
    path: '/settings/wbs-details',
    adminOnly: true,
  },
};

function isVisible(item: NavItem | undefined, isAdminUser: boolean): boolean {
  if (!item) return false;
  if (item.adminOnly && !isAdminUser) return false;
  return true;
}

function filterChildren(children: string[] | undefined, isAdminUser: boolean): string[] {
  if (!children) return [];
  return children.filter((id) => isVisible(ALL_ITEMS[id], isAdminUser));
}

export function buildNavItems(isAdminUser: boolean): {
  items: Record<string, NavItem>;
  rootItemId: string;
} {
  const items: Record<string, NavItem> = {};

  for (const [id, item] of Object.entries(ALL_ITEMS)) {
    if (!isVisible(item, isAdminUser)) continue;

    if (item.children) {
      const visibleChildren = filterChildren(item.children, isAdminUser);
      if (visibleChildren.length === 0 && id !== ROOT_ID) continue;
      items[id] = { ...item, children: visibleChildren };
    } else {
      items[id] = { ...item };
    }
  }

  if (!items[ROOT_ID]) {
    items[ROOT_ID] = {
      name: 'Navigation',
      children: filterChildren(['employee-project-models'], isAdminUser),
    };
  }

  return { items, rootItemId: ROOT_ID };
}

export interface NavSearchItem {
  id: string;
  name: string;
  path: string;
  group: string;
}

function findParentId(targetId: string, items: Record<string, NavItem>): string | null {
  for (const [id, item] of Object.entries(items)) {
    if (item.children?.includes(targetId)) return id;
  }
  return null;
}

function getSectionName(itemId: string, items: Record<string, NavItem>): string {
  let sectionId = itemId;
  let parentId = findParentId(sectionId, items);
  while (parentId && parentId !== ROOT_ID) {
    sectionId = parentId;
    parentId = findParentId(sectionId, items);
  }
  return items[sectionId]?.name ?? 'Navigation';
}

export function getNavSearchItems(
  items: Record<string, NavItem>,
  rootItemId: string = ROOT_ID
): NavSearchItem[] {
  const searchItems: NavSearchItem[] = [];

  function walk(itemId: string) {
    const item = items[itemId];
    if (!item) return;

    if (item.path) {
      searchItems.push({
        id: itemId,
        name: item.name,
        path: item.path,
        group: getSectionName(itemId, items),
      });
    }

    for (const childId of item.children ?? []) {
      walk(childId);
    }
  }

  for (const childId of items[rootItemId]?.children ?? []) {
    walk(childId);
  }

  return searchItems;
}

export function getNavEmoji(name: string): string {
  const spaceIdx = name.indexOf(' ');
  if (spaceIdx <= 0) return '•';
  return name.slice(0, spaceIdx);
}

export function getActiveItemId(pathname: string, items: Record<string, NavItem>): string | null {
  for (const [id, item] of Object.entries(items)) {
    if (item.path === pathname) return id;
  }
  return null;
}

export function getExpandedIdsForPath(pathname: string, items: Record<string, NavItem>): string[] {
  const activeId = getActiveItemId(pathname, items);
  if (!activeId) return [];

  const expanded: string[] = [];
  const findParent = (targetId: string): string | null => {
    for (const [id, item] of Object.entries(items)) {
      if (item.children?.includes(targetId)) return id;
    }
    return null;
  };

  let current: string | null = activeId;
  while (current) {
    const parent = findParent(current);
    if (parent && parent !== 'nav') {
      expanded.push(parent);
    }
    current = parent;
  }

  return expanded;
}
